import { CommandProps } from '@tiptap/core';
import { Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { EditorState, NodeSelection, TextSelection } from '@tiptap/pm/state';
import { ETiptapAlignmentSelectorKey } from '../../../../types/tiptap.types';
import { CAPTION_NODE_NAME, OutRefBookmarkKind } from '../constants';
import { ZWSP } from '../plugins/getCaptionZwspPlugin';

const insertCaptionCommand =
  () =>
  ({ state, dispatch }: CommandProps) => {
    const { schema } = state;
    const captionType = schema.nodes[CAPTION_NODE_NAME];
    if (!captionType) return false;

    const target = getCaptionTarget(state);
    if (!target) return false;

    const isFigure = target.kind === OutRefBookmarkKind.FIGURE;

    // Insert AFTER image, BEFORE table
    const insertPos = isFigure ? target.pos + target.node.nodeSize : target.pos;

    // Minimal default label; numbering to be added later
    const captionNode = captionType.create({
      bookmarkKind: target.kind,
      textAlign: target.align ?? ETiptapAlignmentSelectorKey.LEFT,
    });

    if (dispatch) {
      let tr = state.tr.insert(insertPos, captionNode);

      // Insert ZWSP for Safari cursor positioning (plugin will maintain it)
      const contentPos = insertPos + 1;
      tr = tr.insertText(ZWSP, contentPos);

      // Place caret after the ZWSP
      tr = tr.setSelection(TextSelection.create(tr.doc, contentPos + 1)).scrollIntoView();

      dispatch(tr);
    }

    return true;
  };

// --- HELPERS ---

type TTarget = { node: PMNode; pos: number; align?: ETiptapAlignmentSelectorKey; kind: OutRefBookmarkKind };

const findAncestorTable = ($pos: ResolvedPos, tableType: PMNode['type']): TTarget | null => {
  for (let d = $pos.depth; d > 0; d--) {
    // d>0 → before(d) is valid
    const node = $pos.node(d);
    if (node.type === tableType) {
      return { node, pos: $pos.before(d), kind: OutRefBookmarkKind.TABLE };
    }
  }
  return null;
};

// IMPORTANT: table captions are always left-aligned. Hence no align for tables.
const getCaptionTarget = (state: EditorState): TTarget | null => {
  const { selection, schema } = state;
  const imageType = schema.nodes.image;
  const tableType = schema.nodes.table;
  if (!imageType || !tableType) return null;

  if (selection instanceof NodeSelection) {
    if (selection.node.type === imageType) {
      const imageAlign = selection.node.attrs.textAlign as ETiptapAlignmentSelectorKey | undefined;
      return { node: selection.node, pos: selection.from, align: imageAlign, kind: OutRefBookmarkKind.FIGURE };
    }
    if (selection.node.type === tableType) {
      // this is very unlikely since tables aren't usually node-selectable
      return { node: selection.node, pos: selection.from, kind: OutRefBookmarkKind.TABLE };
    }
  }

  const inTable = findAncestorTable(selection.$from, tableType);
  if (inTable) return inTable;

  return null;
};

export default insertCaptionCommand;
